import { Injectable, signal, computed } from '@angular/core';

export type DoorStyles = 'SLD' | 'SSDL' | 'SSGL';

export interface Door {
  code: DoorStyles;
  name: string;
}

export interface DoorColor {
  code: string;
  color: string;
}

export interface DoorSelection {
  door: Door;
  doorColor: DoorColor | null;
}

@Injectable({ providedIn: 'root' })
export class DoorService {
  /** 🔹 All available doors */
  private readonly doors: Door[] = [
    { code: 'SLD', name: 'Sliding Acrylic Door' },
    { code: 'SSDL', name: 'Solid Swing Door' },
    { code: 'SSGL', name: 'Swing Door w/ Glass' },
  ];

  /** 🔹 Colors available per door type */
  private readonly colorsByDoor: Record<DoorStyles, DoorColor[]> = {
    SLD: [{ code: 'FRS', color: 'Frosted Acrylic' }, {code: 'CLR', color: 'Clear Acrylic'}],
    SSDL: [
      { code: 'SW6990', color: 'Black' },
      { code: 'SW7005', color: 'White' },
      { code: 'SW7027', color: 'Dark Brown'},
      { code: 'SW7522', color: 'Light Brown'}
    ],
    SSGL: [
      { code: 'SW6990', color: 'Black' },
      { code: 'SW7005', color: 'White' },
      { code: 'SW7027', color: 'Dark Brown'},
      { code: 'SW7522', color: 'Light Brown'}
    ]
  };

  /** ✅ Reactive selection with default = first door + its first color */
  private readonly selected = signal<DoorSelection>({
    door: this.doors[0],
    doorColor: this.colorsByDoor[this.doors[0].code][0],
  });

  /** ✅ Computed list of doors */
  readonly availableDoors = computed(() => this.doors);

  /** ✅ Computed colors for the currently selected door */
  readonly availableColors = computed(() => {
    const currentDoor = this.selected().door.code;
    return this.colorsByDoor[currentDoor] ?? [];
  });

  /** ✅ Getter for the selected door + color */
  readonly selectedDoor = computed(() => this.selected());

  /** ✅ Set a new door and reset to its first color */
  setDoor(doorCode: DoorStyles): void {
    const door = this.doors.find(d => d.code === doorCode);
    if (!door) return;

    const defaultColor = this.colorsByDoor[door.code][0] ?? null;

    this.selected.set({
      door,
      doorColor: defaultColor,
    });
  }

  /** ✅ Set a new color for the current door */
  setDoorColor(colorCode: string): void {
    const currentDoor = this.selected().door.code;
    const color = this.colorsByDoor[currentDoor].find(c => c.code === colorCode) ?? null;

    this.selected.update(sel => ({
      ...sel,
      doorColor: color,
    }));
  }
}
